import { authStore, IdentityService } from '@puzzlemania/auth';
import {
  EconomyService,
  SaveRegistry,
  SaveService,
  WalletSaveModule,
  economyStore,
} from '@puzzlemania/economy';
import { DailyRewardSaveModule } from '@puzzlemania/liveops';
import {
  ProgressionAnalyticsProvider,
  ProgressionSaveModule,
  progressionStore,
  xpDailyRewardKeyForDay,
  xpLevelCompleteKeyForDifficulty,
  type XpGrantRule,
} from '@puzzlemania/progression';
import { AudioService, HowlerAdapter, STOP_FADE_MS, makeChannelResolver } from '@puzzlemania/audio';
import type { UserActivation } from '@puzzlemania/audio';
import type { IAudioAdapter, VolumeSettings } from '@puzzlemania/audio';
import {
  GameContextBuilder,
  LocalSaveModule,
  PluginLoader,
  PluginRegistry,
} from '@puzzlemania/plugins';
import { RemoteConfigService } from '@puzzlemania/remote-config';
import { Logger, SettingsSaveModule } from '@puzzlemania/shared';
import {
  ANALYTICS_EVENTS as BLOCKJAM_ANALYTICS_EVENTS,
  BLOCKJAM_ANIMATION_CUES,
  BLOCKJAM_AUDIO_COOLDOWNS,
  BLOCKJAM_MUSIC_ROTATION,
} from '@puzzlemania/blockjam';
import { WATERSORT_ANALYTICS_EVENTS } from '@puzzlemania/watersort';
import type { AudioPlayOptions, LegalDoc } from '@puzzlemania/core';
import excludedAudioIds, { musicStrides } from 'virtual:audio-exclusions';
import blockjamBgPool from 'virtual:bg-pool/blockjam';
import { makeOpenLegalDoc } from '../lib/openLegalDoc';
import { useShopPopupStore } from '../state/shopPopupStore';
import { createLevelBackgroundService } from './levelBackground';
import { createLevelMusicService } from './levelMusic';
import { gameHostFromPlatform } from './gameHost';
import { createPlatformServices, type PlatformServices } from './createPlatformServices';
import { resolvePlatformServices } from './resolvePlatformServices';
import { grantStartingCoinsIfNew } from './startingGrant';
import { saveValidatorFor } from './saveValidators';
import { AppUserActivation } from './userActivation';

/**
 * Module 34 — pool-driven per-level backgrounds. Built once from the build-time
 * `virtual:bg-pool/*` manifests; injected into every game context. Games without
 * a populated pool (all but Block Jam today) get `null` ⇒ their built-in default.
 */
const getLevelBackground = createLevelBackgroundService({ blockjam: blockjamBgPool });

/**
 * Module 64 — pool-driven per-level music, the same shape as the background
 * service above. The POOL is Block Jam's `BLOCKJAM_MUSIC_ROTATION` — its music
 * ids minus the home track (Module 72), which plays on the music channel but is
 * never a puzzle's. Order matters — it is the rotation order; the exclusions are Module 56's
 * deny-set, and the stride comes from `audio/assignments/<game>.json`. So a
 * track is added, removed or silenced entirely from the Audio Studio, with no
 * second registry to keep in step.
 */
const getLevelMusic = createLevelMusicService(
  {
    blockjam: {
      tracks: BLOCKJAM_MUSIC_ROTATION,
      ...(musicStrides.blockjam === undefined ? {} : { stride: musicStrides.blockjam }),
    },
  },
  excludedAudioIds,
);

/**
 * Game-agnostic XP-granting mechanism (WP-XP.5) needs to know which analytics events
 * mean "level complete"/"daily reward claimed" for each specific game — that mapping
 * lives here (the composition root, which already imports both game packages), not
 * inside @puzzlemania/progression itself.
 */
function buildXpGrantRules(remoteConfig: RemoteConfigService): XpGrantRule[] {
  return [
    {
      eventName: BLOCKJAM_ANALYTICS_EVENTS.LEVEL_COMPLETED,
      getXpAmount: (params) =>
        remoteConfig.getNumber(xpLevelCompleteKeyForDifficulty(String(params.difficulty ?? ''))),
    },
    {
      eventName: WATERSORT_ANALYTICS_EVENTS.LEVEL_COMPLETED,
      getXpAmount: (params) =>
        remoteConfig.getNumber(
          xpLevelCompleteKeyForDifficulty(String(params.difficulty_band ?? '')),
        ),
    },
    {
      eventName: 'daily_reward_claimed',
      // Fires twice per claim (once for coins, once more for gems if any were granted)
      // — gate on currency_type so a single claim never double-grants XP.
      getXpAmount: (params) => {
        if (params.currency_type !== 'coins') return null;
        return remoteConfig.getNumber(xpDailyRewardKeyForDay(Number(params.streak_day ?? 1)));
      },
    },
  ];
}

export interface ComposedServices {
  providers: PlatformServices;
  remoteConfig: RemoteConfigService;
  economy: EconomyService;
  uid: string;
  /** Already hydrated from IDataProvider by composeServices() — construct DailyRewardService with this, never a fresh one. */
  dailyRewardSaveModule: DailyRewardSaveModule;
  /** Already hydrated from IDataProvider by composeServices() — progressionStore is seeded from this, kept persisted via a subscription. */
  progressionSaveModule: ProgressionSaveModule;
  /** Shared across every game launched this app load — built from this same economy/remoteConfig/uid. */
  pluginLoader: PluginLoader;
  /** Same instance the root ErrorBoundary/global handlers were constructed with — gains a real analytics sink here once resolved (non-DEV). */
  logger: Logger;
  /**
   * Module 28 (WP-NAV.3) — opens a legal doc externally (new tab / system
   * browser), logging `platform_legal_opened`. The same function games receive
   * via IGameContext.openLegalDoc; Lobby callers pass `from: 'lobby'`.
   */
  openLegalDoc: (doc: LegalDoc, from: string) => void;
  /**
   * The SAME `makeGameAudio()` result every game launched this app load
   * receives as `IGameContext.audio` — not a second instance. Exposed here so
   * the Settings UI (Lobby's `AppLayout`, and anywhere else outside a running
   * game) can call `setVolume()` on the real service instead of writing
   * straight to `SettingsSaveModule`, which is how a Settings slider became
   * disconnected from actually-playing audio in the first place (2026-08-05
   * audit — see `AudioService.setVolume`'s doc comment in `packages/audio`).
   */
  audio: GameAudioHandle;
}

/** Used only by composeServicesSync()'s test-only fallback — the real boot path always resolves a real uid via IdentityService.signIn(). */
const FALLBACK_UID = 'guest';

/**
 * The app's Settings volumes, in the shape `AudioService` reads. `notification`
 * follows the SFX slider — there is no third slider, and a notification is a
 * one-shot cue like any other SFX.
 */
function currentVolumes(): VolumeSettings {
  const { musicVolume, sfxVolume } = SettingsSaveModule.load();
  return { music: musicVolume, sfx: sfxVolume, notification: sfxVolume };
}

/** What both composition paths hand out as `IGameContext.audio`, and what
 *  `ComposedServices.audio` exposes to the Settings UI outside any game. */
export interface GameAudioHandle {
  play: (key: string, options?: AudioPlayOptions) => void;
  stop: (key: string) => void;
  /**
   * `category` matches `IAudioService.setVolume`'s string signature (games
   * pass it as data); anything other than `'music'` resolves to the SFX
   * channel, same as `AudioService.play`'s own fallback.
   *
   * Persists via `onVolumeChange` below AND, since the 2026-08-05 audit,
   * reaches whatever is already sounding on that channel — a background
   * track started once and looped for the rest of the level would otherwise
   * never hear a slider move at all.
   */
  setVolume: (category: string, volume: number) => void;
}

/**
 * WP-37.4 (Module 37): game audio, routed through the framework.
 *
 * Until now this was a hand-rolled wrapper straight onto `HowlerAdapter`, which
 * meant `AudioService` — with its channels, cooldowns, mute and autoplay queue —
 * was never constructed anywhere in the app and every one of those features was
 * dead code. It also read only `sfxVolume`, so `musicVolume` controlled nothing
 * and `setVolume` was an empty function.
 *
 * What the service is given here:
 *   - channels  resolved LAZILY from the registered plugin manifests, because
 *               `PluginRegistry.register()` runs in `main.tsx` and could fire
 *               after this function does. See `makeChannelResolver`.
 *   - cooldowns merged from each game's own table. Ids are namespaced, so the
 *               spread cannot collide.
 *   - volumes   re-read from `SettingsSaveModule` on every play, preserving
 *               WP-POUR.9's behaviour exactly — including the portal embed
 *               zeroing both sliders for the duration of a rewarded ad.
 *
 * Shared by both composition paths (sync dev/mock and async real-BaaS) — and,
 * as of the 2026-08-05 audit, this exact return value is also what
 * `ComposedServices.audio` hands the Settings UI, so a slider dragged from the
 * Lobby and a cue fired by the running game go through the identical
 * `AudioService` instance rather than two that happen to agree on paper.
 */
/**
 * Where cue files live, relative to however this build is served.
 *
 * WP-41.4: `HowlerAdapter` used to hardcode `/audio/`, which is right for the
 * main site and the Android WebView (both served from the origin root) and
 * wrong for the CrazyGames embed, which is served from a sub-path — so every
 * cue 404'd there while `pnpm build` stayed green.
 *
 * Deriving it from `import.meta.env.BASE_URL` rather than passing it in makes
 * each build correct by construction: the main site's `base: '/'` yields
 * `/audio/` (byte-identical to before) and the portal target's `base: './'`
 * yields `./audio/`, which the browser resolves against the document URL — i.e.
 * the game's own directory. There is nothing left to remember to wire up.
 */
const AUDIO_BASE_URL = `${import.meta.env.BASE_URL}audio/`;

export function makeGameAudio(
  adapter: IAudioAdapter = new HowlerAdapter({ assetBaseUrl: AUDIO_BASE_URL }),
  /** Module 57 — injectable so a test gets its own, un-activated tracker
   *  instead of inheriting the previous test's gesture. The concrete class
   *  rather than `IUserActivation`, because this also installs it; the narrow
   *  interface is what `AudioService` consumes. */
  activation: UserActivation = AppUserActivation,
): GameAudioHandle {
  const service = new AudioService(adapter, undefined, {
    stopFadeMs: STOP_FADE_MS,
    channels: makeChannelResolver(() => PluginRegistry.getAll()),
    cooldowns: { ...BLOCKJAM_AUDIO_COOLDOWNS },
    // Module 56 — cues the Audio Studio excluded. Their mp3s were deleted by
    // `pnpm audio:build`, so this is not belt-and-braces for its own sake: it is
    // what stops every trigger becoming a 404, and what keeps "excluded" telling
    // apart from "broken".
    excluded: excludedAudioIds,
    // Module 57 — installed by `main.tsx` before any await, so a gesture during
    // boot is not lost. Fires `onUserInteraction()` immediately when it already
    // happened, which by this point it usually has.
    activation,
    volumes: currentVolumes,
    onVolumeChange: (channel, volume) => {
      SettingsSaveModule.save(
        channel === 'music' ? { musicVolume: volume } : { sfxVolume: volume },
      );
    },
  });

  // WP-50.11 — warm the cues that ride an animation. Not portal-only: a first
  // win is late in exactly the same way on the main site and on Android.
  service.preload(BLOCKJAM_ANIMATION_CUES);

  // Module 57 — the listeners that used to live here have moved into
  // `AppUserActivation`, installed by the entry before any await. Registering
  // them at composition time meant a gesture made while the app was still
  // booting was never observed, and the music stayed owed until the next one.
  //
  // Installed again here, idempotently, purely as a backstop: an entry that
  // forgot the early call would otherwise fall all the way back to "music never
  // plays", which is silent. This does not restore the window — the entry's call
  // is what closes it — it only guarantees something is watching.
  activation.install();

  return {
    play: (key: string, options?: AudioPlayOptions) => service.play(key, undefined, options),
    // WP-REF.8: real stop, fading rather than cutting — the pour loop ends here.
    stop: (key: string) => service.stop(key),
    setVolume: (category: string, volume: number) => {
      service.setVolume(category === 'music' ? 'music' : 'sfx', volume);
    },
  };
}

/**
 * Synchronous composition — used only as PlatformServicesContext's default value so
 * screens rendered without a PlatformServicesProvider ancestor (existing unit tests
 * that inject their own fakes via props) keep working exactly as before. Skips
 * identity sign-in and wallet hydration, both of which are inherently async.
 */
export function composeServicesSync(overrides: Partial<PlatformServices> = {}): ComposedServices {
  const rawProviders = createPlatformServices(overrides);
  const remoteConfig = new RemoteConfigService(rawProviders.remoteConfig);
  const providers: PlatformServices = {
    ...rawProviders,
    analytics: new ProgressionAnalyticsProvider(
      rawProviders.analytics,
      remoteConfig,
      buildXpGrantRules(remoteConfig),
    ),
  };
  const economy = new EconomyService();
  const openLegalDoc = makeOpenLegalDoc(providers.analytics);
  const openShop = (from: string) => useShopPopupStore.getState().open(from);
  // Constructed ONCE and handed to both the builder (→ every game's
  // IGameContext.audio) and the returned ComposedServices (→ the Settings UI)
  // — two callers of the SAME instance, not two instances that happen to
  // agree (2026-08-05 audit).
  const audio = makeGameAudio();
  const builder = new GameContextBuilder({
    economy,
    remoteConfig,
    analyticsProvider: providers.analytics,
    openLegalDoc,
    openShop,
    audio,
    getLevelBackground,
    getLevelMusic,
    // WP-41.6 — derived from this build's platform profile rather than assumed.
    // The web profile grants everything, so this is behaviourally identical to
    // the builder's own default here; stating it means a capability the registry
    // later revokes reaches the game instead of being silently ignored.
    host: gameHostFromPlatform(),
  });
  return {
    providers,
    remoteConfig,
    economy,
    uid: FALLBACK_UID,
    dailyRewardSaveModule: new DailyRewardSaveModule(FALLBACK_UID, providers.data),
    progressionSaveModule: new ProgressionSaveModule(FALLBACK_UID, providers.data),
    pluginLoader: new PluginLoader({ builder }),
    logger: new Logger(true),
    openLegalDoc,
    audio,
  };
}

export interface ComposeResult {
  services: ComposedServices;
  /** Stops the wallet-persistence subscription started by this call. */
  dispose: () => void;
}

/**
 * Real composition path used by PlatformServicesProvider at app boot: signs in via
 * IdentityService to resolve the real uid, hydrates the shared wallet and daily-reward
 * save modules from IDataProvider under that uid before resolving, then keeps the
 * wallet persisted on every credit()/debit() via an economyStore subscription.
 *
 * `logger` is the same instance main.tsx constructed for the root ErrorBoundary/global
 * handlers (so a crash before this resolves still logs) — once the real Supabase
 * Analytics provider is available (non-DEV), it becomes this logger's sink too.
 */
export async function composeServices(
  overrides: Partial<PlatformServices> = {},
  logger: Logger = new Logger(true),
): Promise<ComposeResult> {
  const { providers: rawProviders, onEconomyTransaction } = await resolvePlatformServices(
    overrides,
    logger,
  );

  const identity = new IdentityService(rawProviders.auth);
  await identity.signIn();
  const uid = authStore.getState().userId;
  if (!uid) {
    throw new Error('composeServices: IdentityService.signIn() did not resolve a uid');
  }

  const remoteConfig = new RemoteConfigService(rawProviders.remoteConfig);
  const providers: PlatformServices = {
    ...rawProviders,
    analytics: new ProgressionAnalyticsProvider(
      rawProviders.analytics,
      remoteConfig,
      buildXpGrantRules(remoteConfig),
    ),
  };
  if (!import.meta.env.DEV) {
    logger.attachSink(providers.analytics);
  }

  const economy = new EconomyService(onEconomyTransaction);
  const dailyRewardSaveModule = new DailyRewardSaveModule(uid, providers.data);
  const progressionSaveModule = new ProgressionSaveModule(uid, providers.data);

  const walletSaveModule = new WalletSaveModule(providers.data, uid);
  const registry = new SaveRegistry();
  registry.register(walletSaveModule);
  registry.register(dailyRewardSaveModule);
  registry.register(progressionSaveModule);
  // Hydrates all three modules' in-memory state from IDataProvider before any screen
  // renders — DailyRewardService's constructor reads dailyRewardSaveModule.load()
  // synchronously, so this must complete first.
  // 2026-08-14 — a failed save read degrades this ONE module, it does not stop
  // the app. Before `loadAll` was hardened it rejected on the first failure,
  // which rejected this function, which its only caller did not catch — so a
  // transient backend 401 on any one of these three keys left the player on a
  // permanently blank page. See `SaveService.loadAll`.
  //
  // Reported through the logger rather than swallowed: this is exactly the class
  // of fault AUD-022 added a crash sink for, and a silent degrade would mean a
  // player's progress reading as empty with nothing anywhere saying why.
  await new SaveService(registry, providers.data, ({ key, error }) =>
    logger.error(`save_load_failed:${key}`, error),
  ).loadAll();

  const persisted = walletSaveModule.load();
  if (persisted) {
    economyStore.setState({ coins: persisted.coins, gems: persisted.gems });
  }
  const persistedProgression = progressionSaveModule.load();
  if (persistedProgression) {
    progressionStore.setState({ xp: persistedProgression.xp, level: persistedProgression.level });
  }
  // Module 13: Postgres RLS blocks every direct client write to
  // saves/{uid}/modules/economy, so the write-through subscription below would just
  // fail pointlessly once onEconomyTransaction (the real server-validated RPC path)
  // exists — skip it in that case; DEV/Mock mode keeps writing directly as before.
  const disposeEconomy = onEconomyTransaction
    ? () => {}
    : economyStore.subscribe((state) => {
        walletSaveModule.save({ coins: state.coins, gems: state.gems, version: 1 });
      });
  // Progression isn't server-validated currency (unlike the wallet) — no Module 13
  // restriction applies, so this always writes through directly.
  const disposeProgression = progressionStore.subscribe((state) => {
    progressionSaveModule.save({ xp: state.xp, level: state.level, version: 1 });
  });
  const dispose = () => {
    disposeEconomy();
    disposeProgression();
  };

  // WP-45.6 (BUG-005) — a brand-new player's opening balance.
  //
  // Deliberately AFTER `disposeEconomy` is wired, not next to the hydration that
  // computed `persisted`. In the DEV/Mock path that subscription is the ONLY
  // thing that persists the wallet, and `economyStore.subscribe` fires on
  // subsequent changes only — a grant credited one line earlier would update the
  // balance on screen, never be written, and be granted again on the next boot,
  // forever. The real (server-validated) path does not have that problem, since
  // `onEconomyTransaction` was wired when the service was constructed; putting
  // the call here makes both paths persist the grant for the same reason they
  // persist everything else.
  //
  // Nothing downstream is told the number. The UI reads the wallet, and the
  // wallet is simply already non-zero.
  grantStartingCoinsIfNew({ economy, remoteConfig, hasExistingWallet: persisted !== null });

  const openLegalDoc = makeOpenLegalDoc(providers.analytics);
  const openShop = (from: string) => useShopPopupStore.getState().open(from);
  // Constructed ONCE — see composeServicesSync()'s matching comment. Every
  // game this app load gets IGameContext.audio from this exact object, and so
  // does the Settings UI via ComposedServices.audio.
  const audio = makeGameAudio();
  const builder = new GameContextBuilder({
    economy,
    remoteConfig,
    analyticsProvider: providers.analytics,
    // WP-50.6 — `validate` is not optional in practice: without it a tampered
    // or corrupt save reaches the game verbatim (AUD-021). See saveValidators.ts.
    saveModuleFactory: <T>(key: string) =>
      new LocalSaveModule<T>(key, {
        provider: providers.data,
        uid,
        validate: saveValidatorFor<T>(key),
      }),
    openLegalDoc,
    openShop,
    audio,
    getLevelBackground,
    getLevelMusic,
    // WP-41.6 — see composeServicesSync()'s matching comment.
    host: gameHostFromPlatform(),
  });
  const pluginLoader = new PluginLoader({ builder });

  const services: ComposedServices = {
    providers,
    remoteConfig,
    economy,
    uid,
    dailyRewardSaveModule,
    progressionSaveModule,
    pluginLoader,
    logger,
    openLegalDoc,
    audio,
  };
  return { services, dispose };
}
